import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import {
  Calendar,
  Clock,
  Plus,
  Play,
  Pause,
  Trash2,
  CheckCircle,
  AlertCircle,
  Loader2,
  Zap,
  Edit,
} from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { schedulesApi, sitesApi } from '@/services/api'
import { ScheduleModal } from '@/components/modals/ScheduleModal'
import { StatsCardSkeleton, CardSkeleton } from '@/components/ui/skeleton'
import type { Schedule, ScheduleCreate, ScheduleUpdate, PaginatedResponse, Site, ScheduleStats } from '@/types'

const frequencyLabelKeys: Record<string, string> = {
  once: 'schedules.frequency.once',
  hourly: 'schedules.frequency.hourly',
  daily: 'schedules.frequency.daily',
  weekly: 'schedules.frequency.weekly',
  monthly: 'schedules.frequency.monthly',
  custom: 'schedules.frequency.custom',
}

const statusConfig: Record<string, { labelKey: string; color: string; icon: typeof CheckCircle }> = {
  active: { labelKey: 'schedules.status.active', color: 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300', icon: CheckCircle },
  paused: { labelKey: 'schedules.status.paused', color: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300', icon: Pause },
  completed: { labelKey: 'schedules.status.completed', color: 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300', icon: CheckCircle },
  failed: { labelKey: 'schedules.status.failed', color: 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300', icon: AlertCircle },
}

function formatTime(hour: number, minute: number): string {
  return `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`
}

function formatNextRun(nextRunAt: string | null, t: (key: string, options?: Record<string, unknown>) => string): string {
  if (!nextRunAt) return t('schedules.nextRun.notScheduled')
  const date = new Date(nextRunAt)
  const now = new Date()
  const diff = date.getTime() - now.getTime()

  if (diff < 0) return t('schedules.nextRun.overdue')
  if (diff < 60000) return t('schedules.nextRun.lessThanMinute')
  if (diff < 3600000) return t('schedules.nextRun.inMinutes', { count: Math.round(diff / 60000) })
  if (diff < 86400000) return t('schedules.nextRun.inHours', { count: Math.round(diff / 3600000) })
  return date.toLocaleDateString() + ' ' + t('schedules.nextRun.at') + ' ' + formatTime(date.getHours(), date.getMinutes())
}

export function SchedulesPage() {
  const { t } = useTranslation()
  const [modalOpen, setModalOpen] = useState(false)
  const [editingSchedule, setEditingSchedule] = useState<Schedule | null>(null)
  const queryClient = useQueryClient()

  // Fetch schedules
  const { data: schedulesData, isLoading } = useQuery<PaginatedResponse<Schedule>>({
    queryKey: ['schedules'],
    queryFn: () => schedulesApi.list(),
  })

  // Fetch stats
  const { data: stats, isLoading: statsLoading } = useQuery<ScheduleStats>({
    queryKey: ['schedules', 'stats'],
    queryFn: () => schedulesApi.getStats(),
  })

  // Fetch sites for the form
  const { data: sitesData } = useQuery<PaginatedResponse<Site>>({
    queryKey: ['sites'],
    queryFn: () => sitesApi.list({ is_active: true }),
  })

  // Create schedule mutation
  const createMutation = useMutation({
    mutationFn: (data: ScheduleCreate) => schedulesApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['schedules'] })
      setModalOpen(false)
    },
  })

  // Update schedule mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: ScheduleUpdate }) => schedulesApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['schedules'] })
      setModalOpen(false)
      setEditingSchedule(null)
    },
  })

  // Pause mutation
  const pauseMutation = useMutation({
    mutationFn: schedulesApi.pause,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['schedules'] }),
  })

  // Resume mutation
  const resumeMutation = useMutation({
    mutationFn: schedulesApi.resume,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['schedules'] }),
  })

  // Run now mutation
  const runNowMutation = useMutation({
    mutationFn: schedulesApi.runNow,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['schedules'] }),
  })

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: schedulesApi.delete,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['schedules'] }),
  })

  const schedules = schedulesData?.items || []
  const sites = sitesData?.items || []

  const handleOpenCreate = () => {
    setEditingSchedule(null)
    setModalOpen(true)
  }

  const handleOpenEdit = (schedule: Schedule) => {
    setEditingSchedule(schedule)
    setModalOpen(true)
  }

  const handleSubmit = async (data: ScheduleCreate | ScheduleUpdate) => {
    if (editingSchedule) {
      await updateMutation.mutateAsync({ id: editingSchedule.id, data })
    } else {
      await createMutation.mutateAsync(data as ScheduleCreate)
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{t('schedules.title')}</h1>
          <p className="text-muted-foreground">{t('schedules.description')}</p>
        </div>
        <Button onClick={handleOpenCreate} className="animate-fade-in">
          <Plus className="me-2 h-4 w-4" />
          {t('schedules.newSchedule')}
        </Button>
      </div>

      {/* Stats Cards */}
      {statsLoading ? (
        <div className="grid gap-4 md:grid-cols-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <StatsCardSkeleton key={i} />
          ))}
        </div>
      ) : stats && (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4 stagger-children">
          <Card className="card-hover">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">{t('schedules.stats.active')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-600 dark:text-green-400">{stats.active_schedules}</div>
            </CardContent>
          </Card>
          <Card className="card-hover">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">{t('schedules.stats.dueToday')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">{stats.schedules_due_today}</div>
            </CardContent>
          </Card>
          <Card className="card-hover">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">{t('schedules.stats.articlesGenerated')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.total_articles_generated}</div>
            </CardContent>
          </Card>
          <Card className="card-hover">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">{t('schedules.stats.totalSchedules')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.total_schedules}</div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Schedule List */}
      {isLoading ? (
        <div className="grid gap-4">
          {Array.from({ length: 3 }).map((_, i) => (
            <CardSkeleton key={i} />
          ))}
        </div>
      ) : schedules.length === 0 ? (
        <Card className="animate-fade-in">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Calendar className="h-12 w-12 text-muted-foreground mb-4" />
            <p className="text-lg font-medium">{t('schedules.noSchedules')}</p>
            <p className="text-muted-foreground text-center mt-1 max-w-md">
              {t('schedules.noSchedulesDescription')}
            </p>
            <Button onClick={handleOpenCreate} className="mt-4">
              <Plus className="me-2 h-4 w-4" />
              {t('schedules.createSchedule')}
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 stagger-children">
          {schedules.map((schedule) => {
            const defaultStatus = { labelKey: 'schedules.status.unknown', color: 'bg-gray-100 text-gray-700', icon: CheckCircle }
            const statusConf = statusConfig[schedule.status] || defaultStatus
            const StatusIcon = statusConf.icon

            return (
              <Card key={schedule.id} className="card-hover">
                <CardContent className="p-6">
                  <div className="flex items-start justify-between flex-wrap gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-2 flex-wrap">
                        <h3 className="font-semibold text-lg">{schedule.name}</h3>
                        <span className={`px-2 py-0.5 text-xs rounded-full flex items-center gap-1 ${statusConf.color}`}>
                          <StatusIcon className="h-3 w-3" />
                          {t(statusConf.labelKey)}
                        </span>
                      </div>

                      <p className="text-sm text-muted-foreground mb-3 truncate">
                        {schedule.topic_template}
                      </p>

                      <div className="flex flex-wrap gap-x-4 gap-y-2 text-sm">
                        <span className="flex items-center gap-1 text-muted-foreground">
                          <Clock className="h-4 w-4" />
                          {t(frequencyLabelKeys[schedule.frequency] || 'schedules.frequency.custom')} {t('schedules.at')} {formatTime(schedule.hour, schedule.minute)}
                        </span>
                        <span className="text-muted-foreground">
                          {t('schedules.site')}: <span className="font-medium">{schedule.site_name || t('schedules.unknown')}</span>
                        </span>
                        <span className="text-muted-foreground">
                          {t('schedules.generated')}: <span className="font-medium">{schedule.articles_generated}</span> {t('schedules.articles')}
                        </span>
                        {schedule.next_run_at && schedule.status === 'active' && (
                          <span className="text-muted-foreground">
                            {t('schedules.next')}: <span className="font-medium">{formatNextRun(schedule.next_run_at, t)}</span>
                          </span>
                        )}
                      </div>

                      {schedule.last_error && (
                        <div className="mt-2 p-2 bg-red-50 dark:bg-red-900/20 rounded text-sm text-red-700 dark:text-red-300">
                          {t('schedules.lastError')}: {schedule.last_error}
                        </div>
                      )}
                    </div>

                    <div className="flex items-center gap-2 flex-wrap">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleOpenEdit(schedule)}
                        className="touch-target"
                      >
                        <Edit className="h-4 w-4 md:me-1" />
                        <span className="hidden md:inline">{t('app.edit')}</span>
                      </Button>
                      {schedule.status === 'active' && (
                        <>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => runNowMutation.mutate(schedule.id)}
                            disabled={runNowMutation.isPending}
                            className="touch-target"
                          >
                            {runNowMutation.isPending ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <>
                                <Zap className="h-4 w-4 md:me-1" />
                                <span className="hidden md:inline">{t('schedules.runNow')}</span>
                              </>
                            )}
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => pauseMutation.mutate(schedule.id)}
                            disabled={pauseMutation.isPending}
                            className="touch-target"
                          >
                            <Pause className="h-4 w-4" />
                          </Button>
                        </>
                      )}
                      {schedule.status === 'paused' && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => resumeMutation.mutate(schedule.id)}
                          disabled={resumeMutation.isPending}
                          className="touch-target"
                        >
                          <Play className="h-4 w-4 md:me-1" />
                          <span className="hidden md:inline">{t('schedules.resume')}</span>
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          if (confirm(t('schedules.confirmDelete'))) {
                            deleteMutation.mutate(schedule.id)
                          }
                        }}
                        disabled={deleteMutation.isPending}
                        className="touch-target"
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )
          })}
        </div>
      )}

      {/* Schedule Modal */}
      <ScheduleModal
        open={modalOpen}
        onOpenChange={(open) => {
          setModalOpen(open)
          if (!open) setEditingSchedule(null)
        }}
        schedule={editingSchedule}
        sites={sites}
        onSubmit={handleSubmit}
        isSubmitting={createMutation.isPending || updateMutation.isPending}
      />
    </div>
  )
}
